import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';
import { GLASS_TAB_TRIGGER_CLASS } from '../../../components/ui/tabs';
import { cn } from '../../../lib/utils';
import { projectColorClass } from '../project-color';

/** Chuyển sang `lib/` khi trang Hiệu suất cũng cần (2026-10-07); giữ lối import cũ. */
export { useNarrowScreen } from '../../../lib/use-narrow-screen';

/**
 * Nút "đang mở dự án nào" ở màn hẹp — mở danh sách dự án trượt từ trái.
 *
 * Trên màn rộng việc này do hai thứ làm: nhãn tab Điều lệ (mã + tên) và thanh
 * bên. Ở 390px cả hai cùng không có chỗ — nhãn tab dài ăn gần hết dải tab và
 * đẩy nút "còn N mục" ra sau nhóm tiện ích (người dùng báo trên iPhone,
 * 2026-09-30), còn thanh bên gấp vẫn lấy 64px mỗi dòng. Gộp vào một nút: nó
 * nói tên dự án **và** là lối sang dự án khác.
 */
export function ProjectSwitcherButton({
  projectKey,
  name,
  color,
  onClick,
}: {
  readonly projectKey: string;
  readonly name: string;
  readonly color: string | null | undefined;
  readonly onClick: () => void;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="glass-bar inline-flex h-9 min-w-0 items-center rounded-full p-0.75">
      <button
        type="button"
        aria-haspopup="dialog"
        // Tour mở ngăn này để tới nút "Tất cả dự án" bên trong (`pressVia`, APM-MBAR-c).
        data-tour="project-drawer-open"
        aria-label={`${t('board.chooseProject')}: ${projectKey} · ${name}`}
        title={`${projectKey} · ${name}`}
        className={cn(
          GLASS_TAB_TRIGGER_CLASS,
          'min-w-0 gap-2 px-3 font-semibold text-foreground',
          projectColorClass(color).tab,
        )}
        onClick={onClick}
      >
        <span className="shrink-0 font-mono text-[11px] uppercase text-muted-foreground">
          {projectKey}
        </span>
        <span className="min-w-0 truncate">{name}</span>
        <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      </button>
    </div>
  );
}

/**
 * Danh sách dự án trượt ra từ mép trái — thay thanh bên ở màn hẹp.
 *
 * Cổng ra `document.body` vì cùng lý do với `Dialog`: `position: fixed` bám vào
 * tổ tiên nào có `backdrop-filter`, và trang này đầy kính mờ.
 *
 * Không dùng `Dialog` (hộp giữa màn hình): danh sách dự án là một **cột**, dài
 * theo số dự án, và trượt từ đúng phía mà trên màn rộng nó vẫn đứng.
 */
export function ProjectDrawer({
  open,
  onClose,
  children,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement | null>(null);
  /*
    Dựng ở vị trí đóng rồi mới trượt vào: phần tử vừa gắn vào DOM đã ở sẵn vị
    trí cuối thì `transition` không có gì để chạy.
  */
  const [entered, setEntered] = useState(false);
  /*
    `onClose` qua ref: chỗ gọi truyền một lambda mới mỗi lần vẽ, và effect bên
    dưới chạy lại theo nó thì focus bị giật về khung sau mỗi lần trang vẽ lại.
  */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      // Trả focus về nút đã mở nó — không thì bàn phím rơi về đầu trang.
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden
        className={cn(
          'absolute inset-0 popup-scrim transition-opacity duration-200',
          entered ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={t('board.chooseProject')}
        tabIndex={-1}
        className={cn(
          'glass-menu glass-popup-sm on-scrim absolute inset-y-0 left-0 flex w-[min(20rem,85vw)] flex-col gap-2 rounded-r-2xl p-3 outline-none',
          // Tai thỏ và thanh home của iPhone: danh sách không được nằm dưới chúng.
          'pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))]',
          'motion-safe:transition-transform motion-safe:duration-200',
          entered ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-9 items-center justify-between gap-2">
          <span className="text-sm font-semibold">{t('board.chooseProject')}</span>
          <button
            type="button"
            aria-label={t('common.close')}
            title={t('common.close')}
            className={cn(GLASS_TAB_TRIGGER_CLASS, 'shrink-0 px-2.5')}
            onClick={onClose}
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
